"use client";

import { FormEvent, useState } from "react";
import { Send } from "lucide-react";
import { Button } from "@/client/components/ui/button";

type FormState = "idle" | "submitting" | "sent" | "error";

export function DemoRequestForm() {
  const [state, setState] = useState<FormState>("idle");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState("submitting");
    setError(null);

    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());

    try {
      const response = await fetch("/api/contact/demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Demo request could not be sent.");
      }

      form.reset();
      setState("sent");
    } catch (submitError) {
      setState("error");
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Demo request could not be sent."
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-warm-200 bg-card p-6 shadow-sm md:p-8">
      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm font-medium text-warm-800">
          Name
          <input
            name="name"
            required
            maxLength={120}
            className="mt-2 w-full rounded-md border border-warm-200 bg-warm-50 px-4 py-3 text-base text-warm-950 outline-none transition focus:ring-2 focus:ring-espresso-600 focus:ring-offset-2 focus:ring-offset-background"
          />
        </label>
        <label className="text-sm font-medium text-warm-800">
          Work email
          <input
            name="email"
            type="email"
            required
            maxLength={180}
            className="mt-2 w-full rounded-md border border-warm-200 bg-warm-50 px-4 py-3 text-base text-warm-950 outline-none transition focus:ring-2 focus:ring-espresso-600 focus:ring-offset-2 focus:ring-offset-background"
          />
        </label>
        <label className="text-sm font-medium text-warm-800">
          Funeral home
          <input
            name="funeralHomeName"
            required
            maxLength={180}
            className="mt-2 w-full rounded-md border border-warm-200 bg-warm-50 px-4 py-3 text-base text-warm-950 outline-none transition focus:ring-2 focus:ring-espresso-600 focus:ring-offset-2 focus:ring-offset-background"
          />
        </label>
        <label className="text-sm font-medium text-warm-800">
          Annual case volume
          <input
            name="caseVolume"
            maxLength={80}
            className="mt-2 w-full rounded-md border border-warm-200 bg-warm-50 px-4 py-3 text-base text-warm-950 outline-none transition focus:ring-2 focus:ring-espresso-600 focus:ring-offset-2 focus:ring-offset-background"
          />
        </label>
      </div>

      <label className="mt-4 block text-sm font-medium text-warm-800">
        Notes
        <textarea
          name="notes"
          rows={4}
          maxLength={1200}
          className="mt-2 w-full resize-none rounded-md border border-warm-200 bg-warm-50 px-4 py-3 text-base text-warm-950 outline-none transition focus:ring-2 focus:ring-espresso-600 focus:ring-offset-2 focus:ring-offset-background"
        />
      </label>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button
          type="submit"
          size="xl"
          disabled={state === "submitting" || state === "sent"}
        >
          <Send size={17} />
          {state === "submitting"
            ? "Sending..."
            : state === "sent"
              ? "Request sent"
              : "Request a demo"}
        </Button>
        <p role="status" className="text-sm text-warm-700">
          {state === "sent"
            ? "Thanks. We will follow up by email."
            : state === "error"
              ? error
              : "No payment details needed for a demo request."}
        </p>
      </div>
    </form>
  );
}
